import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sun, Loader2, CalendarCheck, CalendarX, Minus, Plus } from 'lucide-react';
import { CARD, LABEL, Btn, Pill, cx } from '../contacts/ui';

const API = process.env.REACT_APP_BACKEND_URL;

// One tap: open early-stage leads, newest first, skipping anyone already booked in GoHighLevel.
export const DailyCalls = ({ headers, onStarted }) => {
  const [info, setInfo] = useState(null);
  const [target, setTarget] = useState(30);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/calling/daily`, { headers }).then((r) => { setInfo(r.data); setTarget(r.data.daily_target); }).catch(() => {});
  }, [headers]);
  const timer = React.useRef(null);
  const save = (n) => {
    const v = Math.max(1, Math.min(300, Number(n) || 1));
    setTarget(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { const r = await axios.put(`${API}/api/calling/daily/settings`, { daily_target: v }, { headers }); setInfo((i) => ({ ...i, daily_target: r.data.daily_target, left_today: Math.max(r.data.daily_target - (i?.calls_today || 0), 0) })); } catch { toast.error('Could not save your daily goal'); }
    }, 500);
  };
  const build = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/calling/daily/build`, {}, { headers });
      const b = r.data.build_stats || {};
      toast.success(`${b.added} GoHighLevel leads ready to call`, { duration: 9000, description: [b.booked && `${b.booked} skipped, already booked`, b.waiting && `${b.waiting} waiting for their 2-day retry`, b.no_mobile && `${b.no_mobile} with no mobile`, b.more_waiting && `${b.more_waiting} more after your goal`].filter(Boolean).join(' · ') || undefined });
      onStarted(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not build today\u2019s calls'); setBusy(false); }
  };
  const pct = info ? Math.min(100, Math.round(100 * info.calls_today / Math.max(info.daily_target, 1))) : 0;
  return (
    <section className={cx(CARD, 'relative overflow-hidden p-4 sm:p-5')} data-testid="daily-calls-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={cx(LABEL, 'flex items-center gap-1.5')}><Sun className="h-3.5 w-3.5 text-[var(--amber)]" />Today's calls</p>
          <p className="max-w-md text-sm text-[var(--ink-2)]">GoHighLevel leads tagged "MMS lead" and "refinance" from the last 30 days, newest first. Anyone booked in GoHighLevel (or seen in the last 7 days), including under the same mobile or email, is skipped, and each lead is re-checked just before you call. No answer comes back in {info?.retry_days || 2} days, and leads stop after {info?.max_attempts || 10} tries.</p>
        </div>
        {info && <Pill className={info.calendar_connected ? 'bg-[var(--green-bg)] text-[var(--green-ink)]' : 'bg-[var(--amber-bg)] text-[var(--amber-ink)]'} data-testid="daily-calendar-status">
          {info.calendar_connected ? <CalendarCheck className="h-3.5 w-3.5" /> : <CalendarX className="h-3.5 w-3.5" />}{info.calendar_connected ? 'GoHighLevel calendar connected' : 'Calendar not connected'}</Pill>}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div>
          <p className="mb-1 text-xs font-semibold text-[var(--muted)]">Calls I'm happy to make today</p>
          <div className="inline-flex items-center rounded-full border border-[var(--line)] bg-[var(--surface-2)] p-1">
            <button type="button" onClick={() => save(target - 5)} aria-label="Fewer calls" data-testid="daily-target-minus" className="rounded-full p-1.5 text-[var(--ink-2)] hover:bg-[var(--hover)]"><Minus className="h-4 w-4" /></button>
            <input type="number" min={1} max={300} value={target} onChange={(e) => setTarget(e.target.value)} onBlur={(e) => save(e.target.value)} data-testid="daily-target-input"
              className="w-14 bg-transparent text-center text-lg font-bold tabular-nums text-[var(--ink)] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none" />
            <button type="button" onClick={() => save(Number(target) + 5)} aria-label="More calls" data-testid="daily-target-plus" className="rounded-full p-1.5 text-[var(--ink-2)] hover:bg-[var(--hover)]"><Plus className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="min-w-[160px] flex-1">
          <p className="mb-1 text-xs font-semibold text-[var(--muted)]" data-testid="daily-progress-text">{info ? `${info.calls_today} of ${info.daily_target} done today` : '…'}</p>
          <div className="h-2 overflow-hidden rounded-full bg-[var(--hover)]"><div className="h-full rounded-full bg-[var(--amber)] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
        </div>
        <Btn tone="solid" size="lg" disabled={busy || !info?.calendar_connected} onClick={build} data-testid="daily-build-btn" className="w-full sm:w-auto">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sun className="h-5 w-5" />}{busy ? 'Checking GoHighLevel…' : "Build today's calls"}
        </Btn>
      </div>
    </section>
  );
};
